import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/components/ui/select";
import { cn } from "@/projects/lib/utils";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type PaginatedResult,
  type PaginationMeta,
} from "@/projects/lib/pagination";

const EMPTY_META: PaginationMeta = {
  count: 0,
  currentPage: 1,
  totalPages: 1,
  next: null,
  previous: null,
};

export type ServerPaginationController<T> = {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  items: T[];
  /** Zero-based index of the first item on the current page (across all pages). */
  startIndex: number;
  meta: PaginationMeta;
  loading: boolean;
  reload: () => void;
};

/**
 * Server-side pagination controller. Refetches when `page`, `pageSize`,
 * `reload()`, or any value in `deps` changes. Resets to page 1 when `deps`
 * change (e.g. filters/search).
 */
export function useServerPagination<T>(
  fetcher: (args: {
    page: number;
    pageSize: number;
  }) => Promise<PaginatedResult<T>>,
  deps: unknown[] = [],
  options?: { initialPageSize?: number; onError?: (error: unknown) => void }
): ServerPaginationController<T> {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(
    options?.initialPageSize ?? DEFAULT_PAGE_SIZE
  );
  const [items, setItems] = useState<T[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcherRef
      .current({ page, pageSize })
      .then(res => {
        if (!cancelled) {
          setItems(res.items);
          setMeta(res.pagination);
        }
      })
      .catch(error => {
        if (!cancelled) {
          onErrorRef.current?.(error);
          setItems([]);
          setMeta(EMPTY_META);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, reloadKey, ...deps]);

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    items,
    startIndex: (page - 1) * pageSize,
    meta,
    loading,
    reload: () => setReloadKey(k => k + 1),
  };
}

/** Build a compact page-number range with ellipsis markers. */
function getPageRange(current: number, totalPages: number): (number | "…")[] {
  const delta = 1;
  const range: (number | "…")[] = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(totalPages - 1, current + delta);

  range.push(1);
  if (left > 2) {
    range.push("…");
  }
  for (let i = left; i <= right; i++) {
    range.push(i);
  }
  if (right < totalPages - 1) {
    range.push("…");
  }
  if (totalPages > 1) {
    range.push(totalPages);
  }
  return range;
}

export function DataPagination<T>({
  pagination,
  itemLabel = "items",
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className,
}: {
  pagination: ServerPaginationController<T>;
  itemLabel?: string;
  pageSizeOptions?: number[];
  className?: string;
}) {
  const { page, setPage, pageSize, setPageSize, meta, items } = pagination;
  const total = meta.count;
  const totalPages = Math.max(1, meta.totalPages);

  const from = total === 0 ? 0 : pagination.startIndex + 1;
  const to = pagination.startIndex + items.length;
  const pages = getPageRange(page, totalPages);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3",
        className
      )}
    >
      <div className="flex items-center gap-4">
        <div className="text-muted-foreground text-xs">
          {total === 0
            ? `No ${itemLabel}`
            : `Showing ${from}–${to} of ${total} ${itemLabel}`}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">Rows</span>
          <Select
            value={String(pageSize)}
            onValueChange={v => setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(opt => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2"
          onClick={() => setPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span
              key={`ellipsis-${i}`}
              className="text-muted-foreground px-1.5 text-sm"
            >
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? "default" : "outline"}
              size="sm"
              className="h-8 min-w-8 px-2"
              onClick={() => setPage(p)}
            >
              {p}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2"
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
